import React from "react";
import { Wrapper } from "../wrapper";
import { Form } from "@components";
import { Grid } from "@material-ui/core";
import Hooks from "@hooks";

export type KiteworksFormValues = {
  firstname: string;
  lastname: string;
  organization: string;
  emailAddress: string;
  phoneNumber: string;
  globalPaymentsSponsor: string;
  globalPaymentsSponsorEmail: string;
  locationOfBusiness: string;
  dataClassification: string;
  typeOfData: string;
  tenant: string;
  accessStart: Date | null;
  accessEnd: Date | null;
  businessJustification: string;
};

type Key = keyof KiteworksFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: KiteworksFormValues) => void;
  open: boolean;
  values: Partial<KiteworksFormValues>;
  readonly?: boolean;
};

export const KiteworksForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  // -----------------------------
  // LOG WHAT PARENT SENDS
  // -----------------------------

  // -----------------------------
  // INITIALIZE MANAGER
  // -----------------------------
  const [current_user] = Hooks.useUser();
  const form = Form.useManager<KiteworksFormValues>({
    firstname: values?.firstname || "",
    lastname: values?.lastname || "",
    organization: values?.organization || "",
    emailAddress: values?.emailAddress || "",
    phoneNumber: values?.phoneNumber || "",

    globalPaymentsSponsor:
      values?.globalPaymentsSponsor ?? current_user?.name ?? "",
    globalPaymentsSponsorEmail:
      values?.globalPaymentsSponsorEmail ?? current_user?.email ?? "",

    locationOfBusiness: values?.locationOfBusiness || "",

    dataClassification: values?.dataClassification || "",
    typeOfData: values?.typeOfData || "",
    tenant: values?.tenant || "",

    accessStart: values?.accessStart || null,
    accessEnd: values?.accessEnd || null,

    businessJustification: values?.businessJustification || "",
  });

  // -----------------------------
  // ENSURE VALUES PRELOAD AFTER API LOAD
  // -----------------------------
  React.useEffect(() => {
    if (values && Object.keys(values).length > 0) {
      console.log("📌 APPLYING VALUES TO FORM:", values);
      form.set(Form.target.VALUES, values);
    }
  }, [values]);

  // Debug: log form values whenever they change
  React.useEffect(() => {
    console.log("📝 CURRENT FORM VALUES:", form.values);
  }, [form.values]);

  // ---------- VALIDATION ----------
  function validateFormValue([name, value]: [Key, any]): Promise<
    [Key, Error | undefined]
  > {
    switch (name) {
      case "firstname":
      case "lastname":
      case "organization":
      case "emailAddress":
      case "phoneNumber":
      case "globalPaymentsSponsor":
      case "globalPaymentsSponsorEmail":
      case "locationOfBusiness":
      case "dataClassification":
      case "typeOfData":
      case "tenant":
        return Promise.resolve([name, Form.Validators.hasValue(value)]);

      default:
        return Promise.resolve([name, undefined]);
    }
  }

  function onFormValueChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);

    console.log("🔄 FIELD CHANGE:", name, value);

    validateFormValue([name, value]).then(([field, error]) => {
      form.set(Form.target.ERROR, [field, error]);
    });
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validateFormValue([name as Key, value])
      )
    ).then((validations: [Key, Error | undefined][]) => {
      const valid = validations.every(([_, error]) => !error);

      if (valid) {
        console.log("🚀 SUBMITTING FORM:", form.values);
        onSubmit(form.values);
      } else {
        validations.forEach(([field, error]) => {
          form.set(Form.target.ERROR, [field, error]);
        });
      }
    });
  }

  // ---------- OPTIONS ----------
  const tenantOptions = [
    "International EMEA - Sensitive Non-PCI Data (LAN / Corp Site)",
    "International EMEA - Sensitive PCI Data (Secure Environment)",
    "North America - Sensitive Non-PCI Data (LAN / Corp Site)",
    "North America - Sensitive PCI Data (Secure Environment)",
  ];
  const dataClassificationOptions = [
    "DC0 - DC0 PUBLIC",
    "DC1 - DC1 CONFIDENTIAL INTERNAL",
    "DC2 - DC2 CONFIDENTIAL RESTRICTED",
    "DC3 - DC3 CONFIDENTIAL SENSITIVE(e.g HIPAA, PCI, GDPR, etc)",
  ];

  const locationOptions = [
    { label: "International", value: "International" },
    { label: "North America", value: "North America" },
  ];

  // ---------- UI ----------
  return (
    <Wrapper
      onSubmit={onFormSubmit}
      onCancel={() => {
        onCancel();
        if (values) {
          console.log("↩️ RESETTING FORM TO ORIGINAL VALUES:", values);
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      open={open}
      title="External Recipient User Information"
      size="md"
      readonly={readonly}
    >
      <Form>
        <Form.Textbox
          name="firstname"
          label="First Name"
          required
          size={6}
          error={form.errors.firstname}
          value={form.values.firstname}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="lastname"
          label="Last Name"
          required
          size={6}
          error={form.errors.lastname}
          value={form.values.lastname}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="organization"
          label="Organization"
          required
          size={6}
          error={form.errors.organization}
          value={form.values.organization}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="emailAddress"
          label="Email Address"
          required
          size={6}
          error={form.errors.emailAddress}
          value={form.values.emailAddress}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="phoneNumber"
          label="Phone Number"
          required
          size={6}
          error={form.errors.phoneNumber}
          value={form.values.phoneNumber}
          onChange={onFormValueChange}
        />

        <Form.Title>Sponsor Information</Form.Title>

        <Form.Textbox
          name="globalPaymentsSponsor"
          label="Global Payments Sponsor"
          required
          size={6}
          error={form.errors.globalPaymentsSponsor}
          value={form.values.globalPaymentsSponsor}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="globalPaymentsSponsorEmail"
          label="Sponsor Email"
          required
          size={6}
          error={form.errors.globalPaymentsSponsorEmail}
          value={form.values.globalPaymentsSponsorEmail}
          onChange={onFormValueChange}
        />

        <Form.Title>Access Information</Form.Title>

        <Form.Radiogroup
          name="locationOfBusiness"
          label="Location of Business"
          required
          size={12}
          error={form.errors.locationOfBusiness}
          options={locationOptions}
          value={form.values.locationOfBusiness}
          onChange={(name, value) =>
            onFormValueChange("locationOfBusiness", value)
          }
        />

        <Form.Dropdown
          name="dataClassification"
          label="Data Classification"
          required
          size={12}
          error={form.errors.dataClassification}
          options={dataClassificationOptions}
          value={form.values.dataClassification}
          onChange={onFormValueChange}
        />

        <Form.Textbox
          name="typeOfData"
          label="Type of Data"
          required
          size={6}
          error={form.errors.typeOfData}
          value={form.values.typeOfData}
          onChange={onFormValueChange}
        />

        <Form.Dropdown
          name="tenant"
          label="Tenant"
          required
          size={12}
          error={form.errors.tenant}
          options={tenantOptions}
          value={form.values.tenant}
          onChange={onFormValueChange}
        />

        <Form.Datebox
          name="accessStart"
          label="Access Start"
          size={6}
          value={form.values.accessStart}
          onChange={onFormValueChange}
        />

        <Form.Datebox
          name="accessEnd"
          label="Access End"
          size={6}
          value={form.values.accessEnd}
          onChange={onFormValueChange}
        />

        <Form.Textarea
          name="businessJustification"
          label="Business Justification"
          size={12}
          value={form.values.businessJustification}
          onChange={onFormValueChange}
        />
      </Form>
    </Wrapper>
  );
};
